/**
 * Clasificación de riesgo de cartera para REPORTE (migración 0392, decisión Hugo 27-oct-2026).
 * Días CALENDARIO de atraso: al día (0) · atraso (1–14) · pre-mora (15–30) · mora (31+).
 * "Mora" en tableros = solo 31+. El estado operativo en_mora (recargos, Hunter) no cambia.
 */
import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'

export type RiesgoCat = 'al_dia' | 'atraso' | 'pre_mora' | 'mora'
export type RiesgoRow = { categoria: RiesgoCat; orden: number; creditos: number; saldo: number; pct: number }

export const RIESGO_META: Record<RiesgoCat, { l: string; sub: string; c: string }> = {
  al_dia: { l: 'Al día', sub: '0 días', c: '#15803D' },
  atraso: { l: 'Atraso', sub: '1–14 días', c: '#CA8A04' },
  pre_mora: { l: 'Pre-mora', sub: '15–30 días', c: '#EA580C' },
  mora: { l: 'Mora', sub: '31+ días', c: '#DC2626' },
}

export function useRiesgoCartera() {
  const [rows, setRows] = useState<RiesgoRow[]>([])
  useEffect(() => {
    supabase.rpc('cartera_riesgo_resumen').then(({ data }) =>
      setRows(((data ?? []) as RiesgoRow[]).map((r) => ({ ...r, creditos: Number(r.creditos), saldo: Number(r.saldo), pct: Number(r.pct) }))))
  }, [])
  const get = (c: RiesgoCat) => rows.find((r) => r.categoria === c)
  const mora = get('mora')
  return { rows, get, mora, cargado: rows.length > 0 }
}

const fmtQ0 = (n: number) => 'Q' + Number(n ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })

/**
 * Panel principal de salud de cartera (BI). Separa visualmente lo sano, lo que está en gestión y la mora:
 * 3 cifras grandes (al día · en gestión 1–30 d · mora 31+), una sola barra proporcional y el detalle.
 */
export function RiesgoCarteraPanel({ rows }: { rows: RiesgoRow[] }) {
  if (rows.length === 0) return null
  const g = (c: RiesgoCat) => rows.find((r) => r.categoria === c) ?? { categoria: c, orden: 0, creditos: 0, saldo: 0, pct: 0 }
  const al = g('al_dia'), at = g('atraso'), pm = g('pre_mora'), mo = g('mora')
  const gestionPct = at.pct + pm.pct
  const gestionQ = at.saldo + pm.saldo
  const total = rows.reduce((s, r) => s + r.saldo, 0)
  const Big = ({ v, l, sub, c }: { v: string; l: string; sub: string; c: string }) => (
    <div style={{ flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 34, fontWeight: 800, lineHeight: 1, color: c, fontVariantNumeric: 'tabular-nums', letterSpacing: -0.5 }}>{v}</div>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--n-800, #222)', marginTop: 6 }}>{l}</div>
      <div style={{ fontSize: 11.5, color: 'var(--n-500, #777)' }}>{sub}</div>
    </div>
  )
  return (
    <div>
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Big v={`${al.pct.toFixed(1)}%`} l="Al día" sub={`${al.creditos} créditos · ${fmtQ0(al.saldo)}`} c={RIESGO_META.al_dia.c} />
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--n-200, #e5e5e5)' }} />
        <Big v={`${gestionPct.toFixed(1)}%`} l="En gestión · 1–30 d" sub={`${at.creditos + pm.creditos} créditos · ${fmtQ0(gestionQ)}`} c={RIESGO_META.pre_mora.c} />
        <div style={{ width: 1, alignSelf: 'stretch', background: 'var(--n-200, #e5e5e5)' }} />
        <Big v={`${mo.pct.toFixed(1)}%`} l="Mora · 31+ días" sub={`${mo.creditos} créditos · ${fmtQ0(mo.saldo)}`} c={RIESGO_META.mora.c} />
      </div>

      {/* Una sola barra proporcional */}
      <div style={{ display: 'flex', height: 14, borderRadius: 999, overflow: 'hidden', margin: '18px 0 12px', background: 'var(--n-100, #f1efe8)' }}>
        {rows.map((r) => r.pct > 0 && (
          <div key={r.categoria} title={`${RIESGO_META[r.categoria].l}: ${r.pct.toFixed(1)}%`}
            style={{ width: `${r.pct}%`, background: RIESGO_META[r.categoria].c, transition: 'width .8s ease' }} />
        ))}
      </div>

      {/* Detalle por categoría */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map((r) => {
          const m = RIESGO_META[r.categoria]
          return (
            <div key={r.categoria} style={{ display: 'grid', gridTemplateColumns: '14px 1fr auto auto', gap: 10, alignItems: 'center', fontSize: 12.5 }}>
              <span style={{ width: 10, height: 10, borderRadius: 3, background: m.c }} />
              <span style={{ color: 'var(--n-800, #222)' }}>{m.l} <span style={{ color: 'var(--n-400, #999)' }}>· {m.sub}</span></span>
              <span style={{ color: 'var(--n-500, #777)', fontVariantNumeric: 'tabular-nums' }}>{r.creditos} · {fmtQ0(r.saldo)}</span>
              <span style={{ fontWeight: 700, color: m.c, minWidth: 46, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{r.pct.toFixed(1)}%</span>
            </div>
          )
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--n-400, #999)', marginTop: 10 }}>
        Cartera viva {fmtQ0(total)} · días calendario desde la cuota vencida más antigua.
      </div>
    </div>
  )
}

/** Franja compacta de 4 categorías (para encabezar tableros). */
export function RiesgoCarteraStrip({ rows, titulo = 'Riesgo de cartera · días calendario' }: { rows: RiesgoRow[]; titulo?: string }) {
  if (rows.length === 0) return null
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>
        {titulo}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
        {rows.map((r) => {
          const m = RIESGO_META[r.categoria]
          return (
            <div key={r.categoria} style={{ padding: '10px 12px', background: 'var(--surface, #fff)', border: '1px solid var(--border)',
              borderRadius: 10, borderLeft: `3px solid ${m.c}` }}>
              <div style={{ fontSize: 10.5, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                {m.l} · {m.sub}
              </div>
              <div style={{ fontSize: 19, fontWeight: 700, color: m.c, fontVariantNumeric: 'tabular-nums' }}>{r.pct.toFixed(1)}%</div>
              <div style={{ fontSize: 11.5, color: 'var(--muted-foreground)', fontVariantNumeric: 'tabular-nums' }}>
                {r.creditos} créditos · {fmtQ0(r.saldo)}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
